import type { StateCreator } from 'zustand';
import type { Account } from '../types';
import { reviewBankApi } from '../services/reviewBankApi';

export interface ReviewBankSlice {
  reviewQueue: Account[];
  reviewIndex: number;
  reviewLoading: boolean;
  fetchReviewQueue: (niche?: string, lang?: string) => Promise<void>;
  nextReviewCard: () => void;
  prevReviewCard: () => void;
  setReviewGrowth: (id: number, growth: number) => Promise<void>;
  setReviewNiche: (id: number, niche: string) => Promise<void>;
  setReviewType: (id: number, accountType: string) => Promise<void>;
  acceptReviewAccount: (id: number) => Promise<void>;
  hideReviewAccount: (id: number, reason?: string) => Promise<void>;
}

export const createReviewBankSlice: StateCreator<ReviewBankSlice> = (set, get) => ({
  reviewQueue: [],
  reviewIndex: 0,
  reviewLoading: false,

  fetchReviewQueue: async (niche, lang) => {
    set({ reviewLoading: true });
    try {
      const queue = await reviewBankApi.list(niche, lang);
      set({ reviewQueue: queue, reviewIndex: 0, reviewLoading: false });
    } catch {
      set({ reviewLoading: false });
    }
  },

  nextReviewCard: () => {
    const { reviewIndex, reviewQueue } = get();
    if (reviewIndex < reviewQueue.length - 1) {
      set({ reviewIndex: reviewIndex + 1 });
    }
  },

  prevReviewCard: () => {
    const { reviewIndex } = get();
    if (reviewIndex > 0) {
      set({ reviewIndex: reviewIndex - 1 });
    }
  },

  setReviewGrowth: async (id, growth) => {
    await reviewBankApi.setGrowth(id, growth);
    get().fetchReviewQueue();
  },

  setReviewNiche: async (id, niche) => {
    await reviewBankApi.setNiche(id, niche);
    get().fetchReviewQueue();
  },

  setReviewType: async (id, accountType) => {
    await reviewBankApi.setType(id, accountType);
    get().fetchReviewQueue();
  },

  acceptReviewAccount: async (id) => {
    await reviewBankApi.accept(id);
    const { reviewQueue, reviewIndex } = get();
    const newQueue = reviewQueue.filter((a) => a.id !== id);
    set({
      reviewQueue: newQueue,
      reviewIndex: Math.min(reviewIndex, Math.max(0, newQueue.length - 1)),
    });
  },

  hideReviewAccount: async (id, reason) => {
    await reviewBankApi.hide(id, reason);
    const { reviewQueue, reviewIndex } = get();
    const newQueue = reviewQueue.filter((a) => a.id !== id);
    set({
      reviewQueue: newQueue,
      reviewIndex: Math.min(reviewIndex, Math.max(0, newQueue.length - 1)),
    });
  },
});
